import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import { useAnalysisSummaryDataContext } from "@/contexts/AnalysisSummaryDataContext";
import { usePreviewGeom } from "@/hooks/map/usePreviewGeom";
import { RootState } from "@/store";
import {
  ATTRIBUTE_ID,
  type DatasetAttributeSchema,
  DatasetSchemaLabel
} from "@/types/dataset";

import {
  attributesToProps,
  type DOMNode,
  domToReact,
  Element,
  HTMLReactParserOptions
} from "html-react-parser";

type LinkClickRef = { current?: (href: string) => void };

const PREVIEW_MAP_HREF_PREFIX = "#preview-map";

const RISK_LEVEL_CLASSES: Record<string, string> = {
  High: "risk-high",
  Moderate: "risk-moderate",
  Low: "risk-low"
};

const CALLOUT_REGEX = /^\[!(INFO)\]/i;
const CALLOUT_PREFIX_REGEX = /^\[!(INFO)\]\n?/i;

const getElementText = (node: Element): string =>
  node.children
    .map((child) => {
      if (child.type === "text")
        return (child as unknown as { data: string }).data;
      if ((child as Element).type === "tag")
        return getElementText(child as Element);
      return "";
    })
    .join("")
    .trim();

type ReplaceFn = NonNullable<HTMLReactParserOptions["replace"]>;

const buildAiSummaryReplace = (linkRef: LinkClickRef): ReplaceFn => {
  const replace: ReplaceFn = (domNode) => {
    if ((domNode as Element).type !== "tag") return;
    const el = domNode as Element;
    const options: HTMLReactParserOptions = { replace };

    if (el.name === "table") {
      return (
        <div className="overflow-x-auto">
          <table {...attributesToProps(el.attribs)}>
            {domToReact(el.children as DOMNode[], options)}
          </table>
        </div>
      );
    }

    if (el.name === "h3" && getElementText(el) === "General Summary") {
      return (
        <h3 className="general-summary-heading">
          {domToReact(el.children as DOMNode[], options)}
        </h3>
      );
    }

    if (el.name === "blockquote") {
      const blockText = getElementText(el);
      const match = blockText.match(CALLOUT_REGEX);
      if (match) {
        const calloutType = match[1].toLowerCase();
        let prefixStripped = false;

        const calloutReplace: HTMLReactParserOptions["replace"] = (
          node,
          index
        ) => {
          if (!prefixStripped && node.type === "text") {
            const textNode = node as unknown as { data: string };
            const stripped = textNode.data.replace(CALLOUT_PREFIX_REGEX, "");
            if (stripped !== textNode.data) {
              prefixStripped = true;
              return stripped ? <>{stripped}</> : <></>;
            }
          }
          return replace(node, index);
        };

        return (
          <blockquote className={`callout callout-${calloutType}`} role="note">
            {domToReact(el.children as DOMNode[], { replace: calloutReplace })}
          </blockquote>
        );
      }
    }

    if (el.name === "strong") {
      const riskClass = RISK_LEVEL_CLASSES[getElementText(el)];
      if (riskClass) {
        return (
          <strong className={riskClass}>
            {domToReact(el.children as DOMNode[], options)}
          </strong>
        );
      }
    }

    if (el.name === "a") {
      const { href, ...rest } = el.attribs;
      if (!href) return;
      const handler = linkRef.current;
      if (!handler) return;
      const props = attributesToProps(rest);
      return (
        <button
          {...props}
          className="preview-map-link"
          onClick={(e) => {
            e.preventDefault();
            linkRef.current?.(href);
          }}
        >
          {domToReact(el.children as DOMNode[], options)}
        </button>
      );
    }
  };

  return replace;
};

interface UseMarkdownSummaryProps {
  isExpanded: boolean;
  onCollapse?: () => void;
}

export const useMarkdownSummary = ({
  isExpanded,
  onCollapse
}: UseMarkdownSummaryProps): { replace: HTMLReactParserOptions["replace"] } => {
  const { rawDatasets } = useSelector((state: RootState) => state.dataset);
  const { spatialAnalysisDatasetId } = useAnalysisSummaryDataContext();

  const columns = useMemo(
    () => rawDatasets[spatialAnalysisDatasetId]?.columns ?? [],
    [rawDatasets, spatialAnalysisDatasetId]
  );

  const attributes = useMemo(
    () =>
      columns.map(
        (col) =>
          ({
            ...col,
            attribute_name: col.name
          }) as unknown as DatasetAttributeSchema
      ),
    [columns]
  );

  const { handlePreviewClick } = usePreviewGeom({
    handleClose: () => {},
    selectedDatasetId: spatialAnalysisDatasetId || null,
    attributes
  });

  const handleMarkdownLinkClick = (href: string) => {
    if (!href.startsWith(PREVIEW_MAP_HREF_PREFIX)) return;
    const params = new URLSearchParams(href.split("?")[1] ?? "");
    const value = params.get("value");
    const attributeName = params.get("attribute_name");
    if (!value) return;

    const dataset = rawDatasets[spatialAnalysisDatasetId];
    const column = dataset?.columns.find(
      (col) => col.name === attributeName || col.code === attributeName
    );

    if (isExpanded) onCollapse?.();

    if (column?.code === ATTRIBUTE_ID.catchment_id && dataset) {
      const idIdx = dataset.columns.findIndex(
        (col) => col.code === ATTRIBUTE_ID.catchment_id
      );
      const geomIdx = dataset.columns.findIndex(
        (col) => col.code === "catchment_geometry"
      );

      if (idIdx !== -1 && geomIdx !== -1) {
        const matchedRow = dataset.rows.find(
          (row) => String(row[idIdx]) === value
        );
        const wkt = matchedRow?.[geomIdx];
        if (typeof wkt === "string" && wkt) {
          handlePreviewClick(wkt, "wkt");
          return;
        }
      }
    }

    const isGridLike =
      column?.data_label === DatasetSchemaLabel.H3 ||
      column?.data_label === DatasetSchemaLabel.GEOHASH;
    const type = isGridLike ? "grid" : "wkt";

    handlePreviewClick(value, type);
  };

  const linkClickRef = useRef(handleMarkdownLinkClick);
  linkClickRef.current = handleMarkdownLinkClick;
  const replace = useMemo(() => buildAiSummaryReplace(linkClickRef), []);

  return { replace };
};
